import { useCallback, useEffect, useMemo, useState } from "react";
import {
  COLOR_CODING_NONE,
  type ColorCodingMetricType,
} from "@models/ColorCoding";
import {
  getTemplatesForMetricType,
  isColorTemplateMismatch,
} from "@utils/color-coding-utils";

interface UseColorCodingArgs {
  // Currently selected template id from config ("none"/undefined = off).
  templateId: string | null | undefined;
  // Metric type derived from the visualization's current question type.
  metricType: ColorCodingMetricType | null;
  // Persists a new template selection (dispatches the slice action).
  onTemplateChange: (templateId: string) => void;
}

/**
 * Shared Color Coding config logic for the Table and Crosstab drawers.
 */
export const useColorCoding = ({
  templateId,
  metricType,
  onTemplateChange,
}: UseColorCodingArgs) => {
  const [showMismatchMessage, setShowMismatchMessage] = useState(false);

  const templateOptions = useMemo(() => {
    const matching = getTemplatesForMetricType(metricType);
    return [
      { value: COLOR_CODING_NONE, label: "None" },
      ...matching.map((t) => ({ value: t.id, label: t.displayName })),
    ];
  }, [metricType]);

  useEffect(() => {
    if (isColorTemplateMismatch(templateId, metricType)) {
      onTemplateChange(COLOR_CODING_NONE);
      setShowMismatchMessage(true);
    }
  }, [templateId, metricType, onTemplateChange]);

  const handleTemplateChange = useCallback(
    (value: string) => {
      setShowMismatchMessage(false);
      onTemplateChange(value);
    },
    [onTemplateChange],
  );

  return {
    selectedTemplateId: templateId || COLOR_CODING_NONE,
    templateOptions,
    showMismatchMessage,
    handleTemplateChange,
  };
};
